/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  CreateFlowDto,
  FlowDto,
  FlowSummaryDto,
  SaveFlowDraftDto,
  UpdateFlowDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary The workspace's flows, most recently edited first.
 */
export const listFlows = (id: string, signal?: AbortSignal) => {
  return customInstance<FlowSummaryDto[]>({
    url: `/api/v1/workspaces/${id}/flows`,
    method: 'GET',
    signal,
  });
};

export const getListFlowsQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/flows`] as const;
};

export const getListFlowsQueryOptions = <
  TData = Awaited<ReturnType<typeof listFlows>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlows>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListFlowsQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listFlows>>> = ({ signal }) =>
    listFlows(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listFlows>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListFlowsQueryResult = NonNullable<Awaited<ReturnType<typeof listFlows>>>;
export type ListFlowsQueryError = ErrorType<ApiErrorDto>;

export function useListFlows<
  TData = Awaited<ReturnType<typeof listFlows>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlows>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFlows>>,
          TError,
          Awaited<ReturnType<typeof listFlows>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListFlows<
  TData = Awaited<ReturnType<typeof listFlows>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlows>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFlows>>,
          TError,
          Awaited<ReturnType<typeof listFlows>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListFlows<
  TData = Awaited<ReturnType<typeof listFlows>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlows>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The workspace's flows, most recently edited first.
 */

export function useListFlows<
  TData = Awaited<ReturnType<typeof listFlows>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlows>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListFlowsQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create a flow, empty or from a template.
 */
export const createFlow = (
  id: string,
  createFlowDto: BodyType<CreateFlowDto>,
  signal?: AbortSignal,
) => {
  return customInstance<FlowDto>({
    url: `/api/v1/workspaces/${id}/flows`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createFlowDto,
    signal,
  });
};

export const getCreateFlowMutationKey = () => ['createFlow'] as const;

export const getCreateFlowMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createFlow>>,
    TError,
    CreateFlowMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createFlow>>,
  TError,
  CreateFlowMutationVariables,
  TContext
> => {
  const mutationKey = getCreateFlowMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createFlow>>,
    CreateFlowMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return createFlow(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateFlowMutationResult = NonNullable<
  Awaited<ReturnType<typeof createFlow>>
>;
export type CreateFlowMutationBody = BodyType<CreateFlowDto>;
export type CreateFlowMutationError = ErrorType<ApiErrorDto>;
export type CreateFlowMutationVariables = { id: string; data: BodyType<CreateFlowDto> };

/**
 * @summary Create a flow, empty or from a template.
 */
export const useCreateFlow = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createFlow>>,
      TError,
      CreateFlowMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createFlow>>,
  TError,
  CreateFlowMutationVariables,
  TContext
> => {
  return useMutation(getCreateFlowMutationOptions(options), queryClient);
};
/**
 * @summary A flow with its draft graph, the draft's issues and the active version.
 */
export const getFlow = (id: string, flowId: string, signal?: AbortSignal) => {
  return customInstance<FlowDto>({
    url: `/api/v1/workspaces/${id}/flows/${flowId}`,
    method: 'GET',
    signal,
  });
};

export const getGetFlowQueryKey = (id: string, flowId: string) => {
  return [`/api/v1/workspaces/${id}/flows/${flowId}`] as const;
};

export const getGetFlowQueryOptions = <
  TData = Awaited<ReturnType<typeof getFlow>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFlow>>, TError, TData>>;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFlowQueryKey(id, flowId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFlow>>> = ({ signal }) =>
    getFlow(id, flowId, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined && flowId !== null && flowId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getFlow>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetFlowQueryResult = NonNullable<Awaited<ReturnType<typeof getFlow>>>;
export type GetFlowQueryError = ErrorType<ApiErrorDto>;

export function useGetFlow<
  TData = Awaited<ReturnType<typeof getFlow>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFlow>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFlow>>,
          TError,
          Awaited<ReturnType<typeof getFlow>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFlow<
  TData = Awaited<ReturnType<typeof getFlow>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFlow>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFlow>>,
          TError,
          Awaited<ReturnType<typeof getFlow>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFlow<
  TData = Awaited<ReturnType<typeof getFlow>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFlow>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary A flow with its draft graph, the draft's issues and the active version.
 */

export function useGetFlow<
  TData = Awaited<ReturnType<typeof getFlow>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFlow>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetFlowQueryOptions(id, flowId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Rename a flow, or change its status. ACTIVE snapshots the draft as a new
version (refused with the draft's errors if it has any).
 */
export const updateFlow = (
  id: string,
  flowId: string,
  updateFlowDto: BodyType<UpdateFlowDto>,
  signal?: AbortSignal,
) => {
  return customInstance<FlowDto>({
    url: `/api/v1/workspaces/${id}/flows/${flowId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updateFlowDto,
    signal,
  });
};

export const getUpdateFlowMutationKey = () => ['updateFlow'] as const;

export const getUpdateFlowMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateFlow>>,
    TError,
    UpdateFlowMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateFlow>>,
  TError,
  UpdateFlowMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateFlowMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateFlow>>,
    UpdateFlowMutationVariables
  > = (props) => {
    const { id, flowId, data } = props ?? {};

    return updateFlow(id, flowId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateFlowMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateFlow>>
>;
export type UpdateFlowMutationBody = BodyType<UpdateFlowDto>;
export type UpdateFlowMutationError = ErrorType<ApiErrorDto>;
export type UpdateFlowMutationVariables = {
  id: string;
  flowId: string;
  data: BodyType<UpdateFlowDto>;
};

/**
 * @summary Rename a flow, or change its status. ACTIVE snapshots the draft as a new
version (refused with the draft's errors if it has any).
 */
export const useUpdateFlow = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateFlow>>,
      TError,
      UpdateFlowMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateFlow>>,
  TError,
  UpdateFlowMutationVariables,
  TContext
> => {
  return useMutation(getUpdateFlowMutationOptions(options), queryClient);
};
/**
 * @summary Delete a flow and its versions.
 */
export const deleteFlow = (id: string, flowId: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/flows/${flowId}`,
    method: 'DELETE',
    signal,
  });
};

export const getDeleteFlowMutationKey = () => ['deleteFlow'] as const;

export const getDeleteFlowMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFlow>>,
    TError,
    DeleteFlowMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteFlow>>,
  TError,
  DeleteFlowMutationVariables,
  TContext
> => {
  const mutationKey = getDeleteFlowMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteFlow>>,
    DeleteFlowMutationVariables
  > = (props) => {
    const { id, flowId } = props ?? {};

    return deleteFlow(id, flowId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteFlowMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteFlow>>
>;

export type DeleteFlowMutationError = ErrorType<ApiErrorDto>;
export type DeleteFlowMutationVariables = { id: string; flowId: string };

/**
 * @summary Delete a flow and its versions.
 */
export const useDeleteFlow = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteFlow>>,
      TError,
      DeleteFlowMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteFlow>>,
  TError,
  DeleteFlowMutationVariables,
  TContext
> => {
  return useMutation(getDeleteFlowMutationOptions(options), queryClient);
};
/**
 * @summary Save the editor's draft. A draft may have issues; they come back with it.
Answers 409 when someone saved since `revision`.
 */
export const saveFlowDraft = (
  id: string,
  flowId: string,
  saveFlowDraftDto: BodyType<SaveFlowDraftDto>,
  signal?: AbortSignal,
) => {
  return customInstance<FlowDto>({
    url: `/api/v1/workspaces/${id}/flows/${flowId}/draft`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: saveFlowDraftDto,
    signal,
  });
};

export const getSaveFlowDraftMutationKey = () => ['saveFlowDraft'] as const;

export const getSaveFlowDraftMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof saveFlowDraft>>,
    TError,
    SaveFlowDraftMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof saveFlowDraft>>,
  TError,
  SaveFlowDraftMutationVariables,
  TContext
> => {
  const mutationKey = getSaveFlowDraftMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof saveFlowDraft>>,
    SaveFlowDraftMutationVariables
  > = (props) => {
    const { id, flowId, data } = props ?? {};

    return saveFlowDraft(id, flowId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SaveFlowDraftMutationResult = NonNullable<
  Awaited<ReturnType<typeof saveFlowDraft>>
>;
export type SaveFlowDraftMutationBody = BodyType<SaveFlowDraftDto>;
export type SaveFlowDraftMutationError = ErrorType<ApiErrorDto>;
export type SaveFlowDraftMutationVariables = {
  id: string;
  flowId: string;
  data: BodyType<SaveFlowDraftDto>;
};

/**
 * @summary Save the editor's draft. A draft may have issues; they come back with it.
Answers 409 when someone saved since `revision`.
 */
export const useSaveFlowDraft = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof saveFlowDraft>>,
      TError,
      SaveFlowDraftMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof saveFlowDraft>>,
  TError,
  SaveFlowDraftMutationVariables,
  TContext
> => {
  return useMutation(getSaveFlowDraftMutationOptions(options), queryClient);
};
